import React from 'react';
import {StageBandBar} from '../../opportunities/StageBandBar';
import {bandForWinChance, type StageBand} from '../../../../opportunities-tab/opportunityStage';

type Props = {
	/** The account's mapping, live from the graph. */
	bands: StageBand[];
	/** The project's current win chance, as a 0..1 fraction. */
	value: number;
	/** Called with the chosen band's win probability, as a 0..1 fraction. */
	onChange: (value: number) => void;
};

/**
 * The win-probability picker, as one segment per stage in the account's mapping.
 *
 * The highlighted segment is the band the current win chance falls in, so a mid-band value — one set
 * from the free percentage field on Project Settings, say — still lights the band it belongs to rather
 * than nothing. Picking a segment emits that band's representative win probability.
 *
 * 0% and 100% are never segments: they are reached through Mark as Lost / Mark as Won, and offering
 * them here would let the picker convert an opportunity as a side effect of choosing a stage.
 */
export const WinProbabilityStageBands = ({bands, value, onChange}: Props): JSX.Element => {
	const resolved = bandForWinChance(bands, value);
	return (
		<StageBandBar
			value={resolved.kind === 'band' ? resolved.band.id : ''}
			onChange={bandId => {
				const picked = bands.find(band => band.id === bandId);
				if (picked) {
					onChange(picked.winProbability / 100);
				}
			}}
			dataCy="win-probability"
			items={bands.map(band => ({
				value: band.id,
				topText: `${band.winProbability}%`,
				label: band.name,
				dataCy: `win-band-${band.id}`,
			}))}
		/>
	);
};

export default WinProbabilityStageBands;
